import React, { useState } from "react";
import {
  CardContent,
  Typography,
  Divider,
  Box,
  Paper,
  Button,
  Chip,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloseIcon from "@mui/icons-material/Close";

import { useTranslation } from "../../../hooks/useTranslation";
import { useGetLocale } from "@refinedev/core";
import { DeliveryOrder } from "../../orders/utils/types";
import { Row } from "./ValueRow";
import { ErrorOutline } from "@mui/icons-material";
import { Copyable } from "../../../components/copyable/Copyable";

type Props = {
  deliveryOrder: DeliveryOrder;
  orderStatus: string;
  barcode: string;
  warehouseError?: string;
};

export const OrderDelivery = ({
  deliveryOrder,
  orderStatus,
  barcode,
  warehouseError,
}: Props) => {
  const { translate, translateWithoutPrefix } = useTranslation(
    "pages.orders.detail",
  );
  const locale = useGetLocale();

  const [popupOpen, setPopupOpen] = useState(
    deliveryOrder?.cancellationRequestedByBuyer === true,
  );

  const formatDate = (date: string | undefined, locale: string | undefined) => {
    return date ? new Date(date).toLocaleDateString(locale) : "-";
  };

  return (
    <>
      {/* =========================
          CANCELLATION POPUP
      ========================= */}
      {popupOpen && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.55)",
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Paper
            elevation={12}
            sx={{
              width: 440,
              borderRadius: 2,
              p: 3,
              position: "relative",
              borderTop: "4px solid",
              borderColor: "error.main",
            }}
          >
            {/* Close */}
            <Box
              onClick={() => setPopupOpen(false)}
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
                cursor: "pointer",
                color: "grey.500",
                "&:hover": { color: "grey.800" },
              }}
            >
              <CloseIcon fontSize="small" />
            </Box>

            {/* Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                mb: 2,
              }}
            >
              <WarningAmberIcon color="error" fontSize="medium" />
              <Typography variant="h6" fontWeight={700}>
                {translate("cancellationRequestedTitle")}
              </Typography>
            </Box>

            {/* Content */}
            <Typography variant="body2" color="text.secondary" mb={1}>
              {translate("cancellationRequestedLine1")}
            </Typography>

            <Typography variant="body2" color="text.secondary" mb={2}>
              {translate("cancellationRequestedLine2")}
            </Typography>

            <Box
              sx={{
                backgroundColor: "error.lighter",
                borderRadius: 1,
                p: 1.5,
                mb: 3,
              }}
            >
              <Typography variant="body2" fontWeight={600} color="error.main">
                {translate("cancellationRequestedWarning")}
              </Typography>
            </Box>

            {/* Action */}
            <Button
              variant="contained"
              color="error"
              fullWidth
              onClick={() => setPopupOpen(false)}
              sx={{
                fontWeight: 600,
                py: 1.2,
              }}
            >
              {translateWithoutPrefix("buttons.understood")}
            </Button>
          </Paper>
        </Box>
      )}

      {/* =========================
          DELIVERY INFO
      ========================= */}
      <CardContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1,
          }}
        >
          <Typography variant="h6">
            {translate("deliveryInformation")}
          </Typography>

          {deliveryOrder?.cancellationRequestedByBuyer && (
            <Chip
              icon={<ErrorOutline />}
              label={translate("cancellationRequestedTitle")}
              color="error"
              size="small"
              sx={{
                fontWeight: 600,
                "& .MuiChip-icon": {
                  fontSize: "1rem",
                },
              }}
            />
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Row
          label={translate("orderStatus")}
          value={
            orderStatus === "WAREHOUSE_ERROR"
              ? warehouseError
              : translate(`status.${orderStatus}`)
          }
        />

        <Row
          label={translate("purchaseDate")}
          value={formatDate(deliveryOrder.amazonOrder.purchaseDate, locale())}
        />

        <Row
          label={translate("shippingDate")}
          value={
            <>
              {formatDate(deliveryOrder.amazonOrder.earliestShipDate, locale())}
              {" - "}
              {formatDate(deliveryOrder.amazonOrder.latestShipDate, locale())}
            </>
          }
        />

        <Row
          label={translate("deliveryDate")}
          value={
            <>
              {formatDate(
                deliveryOrder.amazonOrder.earliestDeliveryDate,
                locale(),
              )}
              {" - "}
              {formatDate(
                deliveryOrder.amazonOrder.latestDeliveryDate,
                locale(),
              )}
            </>
          }
        />

        <Row
          label={translate("barcode")}
          value={barcode ? <Copyable initialLabel={barcode} /> : "-"}
        />

        <Row
          label={translate("trackingId")}
          value={
            deliveryOrder?.shipment?.trackingNumber ? (
              <Copyable initialLabel={deliveryOrder.shipment.trackingNumber} />
            ) : (
              "-"
            )
          }
        />

        <Row
          label={translate("estimatedTotalDesi")}
          value={deliveryOrder.estimatedTotalDesi}
        />

        <Row
          label={translate("amazonOrderId")}
          value={
            <Copyable initialLabel={deliveryOrder.amazonOrder.amazonOrderId} />
          }
        />
      </CardContent>
    </>
  );
};
